'use client';

import { useState, useEffect } from 'react';
import { SearchBar } from './SearchBar';
import { IMG } from '../../imgPrefix';

interface ConcertsHomeHeroProps {
  /** H1 marketing headline  (e.g. "Experience   unforgettable live concerts"). */
  title?: string;
  /** Tagline rendered directly below the title. */
  subtitle?: string;
  /** Path to the banner image — rendered as a real  `<img>` layered
   *  behind the hero content (not a CSS `background-image`). */
  bannerImage?: string;
  /** Fallback used when `bannerImage` fails to load. */
  bannerFallback?: string;
  /** Pre-fill the SearchBar's search input.  */
  searchValue?: string;
}

/**
 * Concerts HOME hero — a purpose-built variant of `EventsHero` that:
 *
 *   • Renders NO breadcrumb rail (redundant with the section
 *     landing itself).
 *   • Wraps the banner in `.cutout-shape` for the notched-corner
 *     treatment defined in `app/globals.css`.
 *   • Serves the banner as a real DOM `<img>` layered behind the
 *     text (better SEO / accessibility / right-click-save vs a CSS
 *     `background-image`).
 *   • Renders a marketing title + subtitle above the SearchBar.
 *
 * Deep concerts listing pages (`/concerts/city/[slug]`,
 * `/concerts/artist/[slug]`, `/concerts/venue/[slug]`,
 * `/concerts/tournament/[slug]`, `/concerts/type/[slug]`,
 * `/concerts/events`) continue to use the shared `EventsHero`
 * component — they need the breadcrumb rail and the CSS
 * background approach with its responsive rules intact.
 *
 * Styling: reuses the existing `.hero-3-sports` / `.hero-sports-inner`
 * classes (see `app/concerts/eventlist.css`) so the vertical
 * spacing, overlay, and z-index stacking match the other heroes.
 * The `<img>` layer + cutout-shape rules live in the same
 * stylesheet.
 */
const DEFAULT_BANNER = `${IMG}/image/football-img.png`;

export function ConcertsHomeHero({
  title = 'Experience unforgettable live concerts',
  subtitle,
  bannerImage,
  bannerFallback,
  searchValue,
}: ConcertsHomeHeroProps) {
  const [bgImage, setBgImage] = useState(bannerImage || DEFAULT_BANNER);
  const fallback = bannerFallback || DEFAULT_BANNER;

  // Preload the image so an error swaps to the fallback before the
  // browser paints a broken-image icon. Skips when the caller
  // didn't set a bannerImage (or it's already the DEFAULT).
  useEffect(() => {
    if (!bannerImage || bannerImage === DEFAULT_BANNER) return;
    const img = new window.Image();
    img.src = bannerImage;
    img.onerror = () => setBgImage(fallback);
  }, [bannerImage, fallback]);

  return (
    <section className="home-hero hero-3 Component-Widget section-padding" data-highlightable="0">
      <div className="container">
        <div className="cutout-shape cutout-shape-40">
          {/* Banner image layered behind the hero content (see
              `.hero-3-sports-img` in `eventlist.css`). Decorative,
              so `alt=""` + `aria-hidden`. */}
              <div className="cutout-inner">
          <img
            src={bgImage}
            alt=""
            aria-hidden="true"
            className="hero-3-sports-img"
          />
          <div className="h-hero-sports-inner">
            <h1 className="text-white text-center mb-2">{title}</h1>
            {subtitle && (
              <p className="text-white text-center mb-4 fontsize18">{subtitle}</p>
            )}
            <div className="hero-search-wrap hero-form w-100">
              <SearchBar initialSearch={searchValue} />
            </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
